// src/components/dashboard/ChatManagement.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ChatBubbleLeftIcon,
  ClockIcon,
  CpuChipIcon,
  CheckCircleIcon,
  TrashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://api.mythicalarc.com/api'
  : 'http://localhost:8000/api';

const ChatManagement = ({ user }) => {
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState([]);

  useEffect(() => {
    loadChatData();
  }, []);

  const loadChatData = async () => {
    try {
      // Load chat sessions
      const sessionsResponse = await axios.get(`${API_BASE_URL}/chat/sessions`);
      
      // Enhance sessions with mock data for demonstration
      const enhancedSessions = sessionsResponse.data.map(session => ({
        ...session,
        message_count: Math.floor(Math.random() * 20) + 5,
        agent_used: ['WeatherAgent', 'CalculatorAgent', 'FileAgent', null][Math.floor(Math.random() * 4)],
        status: Math.random() > 0.3 ? 'completed' : 'active',
        tokens_used: Math.floor(Math.random() * 1000) + 100,
        avg_response_time: (Math.random() * 3 + 0.5).toFixed(1),
        sentiment: ['positive', 'neutral', 'negative'][Math.floor(Math.random() * 3)]
      }));

      setSessions(enhancedSessions);

      // Calculate stats
      const totalMessages = enhancedSessions.reduce((sum, session) => sum + session.message_count, 0);
      const activeSessions = enhancedSessions.filter(s => s.status === 'active').length;
      const agentUsage = enhancedSessions.filter(s => s.agent_used).length;
      const avgTokens = enhancedSessions.reduce((sum, session) => sum + session.tokens_used, 0) / enhancedSessions.length;

      setStats({
        totalSessions: enhancedSessions.length,
        activeSessions,
        totalMessages,
        agentUsage,
        avgTokens: Math.round(avgTokens),
        avgResponseTime: '1.8s'
      });

    } catch (error) {
      console.error('Failed to load chat data:', error);
      // Load mock data on error
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const loadMockData = () => {
    const mockSessions = [
      {
        id: 1,
        session_name: 'Weather Planning Discussion',
        created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(),
        message_count: 12,
        agent_used: 'WeatherAgent',
        status: 'active',
        tokens_used: 487,
        avg_response_time: '1.2',
        sentiment: 'positive'
      },
      {
        id: 2,
        session_name: 'File Processing Help',
        created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
        message_count: 8,
        agent_used: 'FileAgent',
        status: 'completed',
        tokens_used: 234,
        avg_response_time: '2.1',
        sentiment: 'neutral'
      },
      {
        id: 3,
        session_name: 'Complex Math Problems',
        created_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
        message_count: 15,
        agent_used: 'CalculatorAgent',
        status: 'completed',
        tokens_used: 672,
        avg_response_time: '1.8',
        sentiment: 'positive'
      },
      {
        id: 4,
        session_name: 'General Conversation',
        created_at: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
        updated_at: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
        message_count: 23,
        agent_used: null,
        status: 'completed',
        tokens_used: 892,
        avg_response_time: '1.5',
        sentiment: 'positive'
      }
    ];

    setSessions(mockSessions);
    setStats({
      totalSessions: 4,
      activeSessions: 1,
      totalMessages: 58,
      agentUsage: 3,
      avgTokens: 571,
      avgResponseTime: '1.6s'
    });
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive': return 'text-green-600 bg-green-100';
      case 'negative': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getAgentColor = (agent) => {
    const colors = {
      'WeatherAgent': 'bg-blue-100 text-blue-800',
      'CalculatorAgent': 'bg-purple-100 text-purple-800',
      'FileAgent': 'bg-green-100 text-green-800',
      'NewsAgent': 'bg-orange-100 text-orange-800',
      'ReminderAgent': 'bg-pink-100 text-pink-800'
    };
    return colors[agent] || 'bg-gray-100 text-gray-800';
  };

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this chat session?')) return;
    
    try {
      // In real implementation, call API to delete session
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (error) {
      console.error('Failed to delete session:', error);
    }
  };

  const toggleSessionSelection = (sessionId) => {
    setSelectedSessions(prev => 
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const bulkDeleteSessions = async () => {
    if (selectedSessions.length === 0) return;
    if (!window.confirm(`Delete ${selectedSessions.length} selected sessions?`)) return;
    
    setSessions(prev => prev.filter(s => !selectedSessions.includes(s.id)));
    setSelectedSessions([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Chat Management</h2>
          <p className="text-gray-600">Manage your conversation history and analyze chat patterns</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          {selectedSessions.length > 0 && (
            <button
              onClick={bulkDeleteSessions}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
            >
              Delete Selected ({selectedSessions.length})
            </button>
          )}
          <Link to="/chat" className="btn-primary">
            Start New Chat
          </Link>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChatBubbleLeftIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Sessions</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeSessions}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <CpuChipIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Agent Usage</p>
              <p className="text-2xl font-bold text-gray-900">{stats.agentUsage}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClockIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900">{stats.avgResponseTime}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Session List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Chat Sessions</h3>
            <div className="text-sm text-gray-500">
              {sessions.length} sessions total
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSessions(sessions.map(s => s.id));
                      } else {
                        setSelectedSessions([]);
                      }
                    }}
                    className="rounded border-gray-300"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Session
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Messages
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tokens
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.map((session) => (
                <tr key={session.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedSessions.includes(session.id)}
                      onChange={() => toggleSessionSelection(session.id)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {session.session_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        Created {formatTimeAgo(session.created_at)}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {session.agent_used ? (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAgentColor(session.agent_used)}`}>
                        {session.agent_used}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">No agent</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.message_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {session.tokens_used?.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'active' 
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {session.status}
                      </span>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs rounded-full ${getSentimentColor(session.sentiment)}`}>
                        {session.sentiment}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimeAgo(session.updated_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/chat?session=${session.id}`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => deleteSession(session.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {sessions.length === 0 && (
          <div className="p-12 text-center">
            <ChatBubbleLeftIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No chat sessions</h3>
            <p className="mt-1 text-sm text-gray-500">
              Start your first conversation to see it appear here.
            </p>
            <div className="mt-6">
              <Link to="/chat" className="btn-primary">
                Start Chatting
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatManagement;